// frontend/src/components/layouts/AdminLayout.tsx

import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Squares2X2Icon,
  TagIcon,
  CubeIcon,
  CakeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  WalletIcon,
  ChartBarIcon,
  PhotoIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  SparklesIcon,
  DocumentArrowUpIcon,
  StarIcon as StarIconOutline,
  BanknotesIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  GiftIcon,
  TicketIcon,
  ShoppingCartIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  CreditCardIcon,
  ChevronDownIcon,
  MegaphoneIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  MapPinIcon,
  PlusIcon, // ✅ ADDED: For Place Order
  ShareIcon, // 🔗 Product Share
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const AdminLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isCouponsOpen, setIsCouponsOpen] = useState(false);
  const [isOrdersOpen, setIsOrdersOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (location.pathname.startsWith('/admin/coupons')) setIsCouponsOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/admin/orders')) setIsOrdersOpen(true);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  // ── Orders sub-links (UPDATED: Added Place Order) ─────────────────────────
  const orderSubLinks = [
    { to: '/admin/orders', icon: ClipboardDocumentListIcon, label: 'All Orders', exact: true },
    { to: '/admin/orders/create', icon: PlusIcon, label: 'Place Order', exact: false }, // ✅ ADDED
    { to: '/admin/orders/track', icon: MapPinIcon, label: 'Track Order', exact: false },
    { to: '/admin/orders/return-exchange', icon: ArrowPathIcon, label: 'Return & Exchange', exact: false },
    { to: '/admin/orders/replacements', icon: ArrowsRightLeftIcon, label: 'Replacements', exact: false },
    { to: '/admin/orders/ndr', icon: ExclamationTriangleIcon, label: 'NDR', exact: false },
    { to: '/admin/orders/rto', icon: ArrowPathIcon, label: 'RTO & Returns', exact: false },
  ];

  // ── Coupon sub-links ──────────────────────────────────────────────────────
  const couponSubLinks = [
    { to: '/admin/coupons/free-gift', icon: GiftIcon, label: 'Free Gift' },
    { to: '/admin/coupons/bxgy', icon: ShoppingCartIcon, label: 'BXGY' },
    { to: '/admin/coupons/generic', icon: TicketIcon, label: 'Generic Coupons' },
    { to: '/admin/coupons/post-checkout', icon: CreditCardIcon, label: 'Post Checkout Offer' },
    { to: '/admin/coupons/repeat-user', icon: ArrowPathIcon, label: 'Repeat User Marketing' },
    { to: '/admin/coupons/particular-customer', icon: UserIcon, label: 'Particular Customer Coupon' },
  ];

  // ── All nav links (split into sections) ───────────────────────────────────
  // Section 1: Dashboard to Inventory Purchases (first 6 items)
  const topLinks = [
    { to: '/admin', icon: Squares2X2Icon, label: 'Dashboard', exact: true },
    { to: '/admin/categories', icon: TagIcon, label: 'Categories', exact: false },
    { to: '/admin/age', icon: CakeIcon, label: 'Age Groups', exact: false },
    { to: '/admin/products', icon: CubeIcon, label: 'Products', exact: false },
    { to: '/admin/product-share', icon: ShareIcon, label: 'Product Share', exact: false },
    { to: '/admin/whatsapp-retarget', icon: MegaphoneIcon, label: 'WhatsApp Retarget', exact: false },
    { to: '/admin/warehouse', icon: BuildingStorefrontIcon, label: 'Warehouse', exact: false },
    { to: '/admin/inventory', icon: DocumentArrowUpIcon, label: 'Inventory Purchases', exact: false },
  ];

  // Section 2: Customers (standalone)
  const customerLink = [
    { to: '/admin/customers', icon: UsersIcon, label: 'All Customers', exact: false },
  ];

  // Section 3: Everything else (Employees to Invoice & Charges)
  const bottomLinks = [
    { to: '/admin/employees', icon: UserGroupIcon, label: 'Employees', exact: false },
    { to: '/admin/wallet', icon: WalletIcon, label: 'Wallet', exact: false },
    { to: '/admin/cod-remittance', icon: BanknotesIcon, label: 'COD Remittance', exact: false },
    { to: '/admin/cod-configuration', icon: BanknotesIcon, label: 'COD Configuration', exact: false },
    { to: '/admin/commissions', icon: ChartBarIcon, label: 'Commissions', exact: false },
    { to: '/admin/withdrawals', icon: BanknotesIcon, label: 'Withdrawals', exact: false },
    { to: '/admin/referral-settings', icon: Cog6ToothIcon, label: 'Referral Settings', exact: false },
    { to: '/admin/reports', icon: ChartBarIcon, label: 'Reports', exact: false },
    { to: '/admin/reviews', icon: StarIconOutline, label: 'Reviews', exact: false },
    { to: '/admin/slider', icon: PhotoIcon, label: 'Slider Images', exact: false },
    { to: '/admin/blog', icon: DocumentTextIcon, label: 'Blog Posts', exact: false },
    { to: '/admin/announcements', icon: MegaphoneIcon, label: 'Announcements', exact: false },
    { to: '/admin/contact', icon: EnvelopeIcon, label: 'Contact Page', exact: false },
    { to: '/admin/about', icon: InformationCircleIcon, label: 'About Page', exact: false },
    { to: '/admin/invoice-charges', icon: CreditCardIcon, label: 'Invoice & Charges', exact: false },
  ];

  const isExpanded = isMobile ? isSidebarOpen : (!isCollapsed || isHovered);
  const sidebarW = isExpanded ? 256 : (isMobile ? 0 : 80);
  const isCouponsActive = location.pathname.startsWith('/admin/coupons');
  const isOrdersActive = location.pathname.startsWith('/admin/orders');

  // ── Reusable NavLink ──────────────────────────────────────────────────────
  const NavLink: React.FC<{ to: string; icon: React.ElementType; label: string; exact?: boolean }> =
    ({ to, icon: Icon, label, exact }) => {
      const isActive = exact ? location.pathname === to
        : location.pathname === to || location.pathname.startsWith(to + '/');
      return (
        <Link
          to={to}
          onClick={() => isMobile && setIsSidebarOpen(false)}
          title={!isExpanded ? label : undefined}
          className={`group relative flex items-center py-2.5 rounded-xl text-sm font-medium overflow-hidden
            ${isExpanded ? 'px-4' : 'px-0 justify-center'}
            ${isActive ? 'text-white' : 'text-gray-700 hover:text-[#2D6A4F]'}`}
        >
          <span className={`absolute inset-0 rounded-xl transition-opacity duration-150 ${
            isActive
              ? 'bg-gradient-to-r from-[#2D6A4F] to-[#C27B7F] opacity-100'
              : 'bg-gradient-to-r from-[#D4EDDA] to-[#F9E2E7] opacity-0 group-hover:opacity-100'
          }`} />
          <Icon className={`relative z-10 h-5 w-5 shrink-0 transition-transform duration-150
            ${isExpanded ? 'mr-3' : ''}
            ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-[#2D6A4F] group-hover:scale-110'}`}
          />
          {isExpanded && <span className="relative z-10 flex-1 whitespace-nowrap">{label}</span>}
          {isActive && isExpanded && (
            <>
              <SparklesIcon className="relative z-10 h-4 w-4 text-white animate-pulse shrink-0" />
              <ChevronRightIcon className="relative z-10 h-4 w-4 text-white ml-1 shrink-0" />
            </>
          )}
          <span className={`absolute right-2 w-1.5 h-1.5 rounded-full transition-opacity duration-150 ${
            isActive && isExpanded ? 'bg-white opacity-100' : 'bg-[#C5A059] opacity-0 group-hover:opacity-100'
          }`} />
        </Link>
      );
    };

  // ── Reusable Dropdown ─────────────────────────────────────────────────────
  const NavDropdown: React.FC<{
    icon: React.ElementType;
    label: string;
    isActive: boolean;
    isOpen: boolean;
    onToggle: () => void;
    onCollapsedClick: () => void;
    subLinks: { to: string; icon: React.ElementType; label: string; exact?: boolean }[];
  }> = ({ icon: Icon, label, isActive, isOpen, onToggle, onCollapsedClick, subLinks }) => (
    <div>
      <button
        onClick={() => isExpanded ? onToggle() : onCollapsedClick()}
        title={!isExpanded ? label : undefined}
        className={`group relative w-full flex items-center py-2.5 rounded-xl text-sm font-medium overflow-hidden
          ${isExpanded ? 'px-4' : 'px-0 justify-center'}
          ${isActive ? 'text-white' : 'text-gray-700 hover:text-[#2D6A4F]'}`}
      >
        <span className={`absolute inset-0 rounded-xl transition-opacity duration-150 ${
          isActive
            ? 'bg-gradient-to-r from-[#2D6A4F] to-[#C27B7F] opacity-100'
            : 'bg-gradient-to-r from-[#D4EDDA] to-[#F9E2E7] opacity-0 group-hover:opacity-100'
        }`} />
        <Icon className={`relative z-10 h-5 w-5 shrink-0 transition-transform duration-150
          ${isExpanded ? 'mr-3' : ''}
          ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-[#2D6A4F] group-hover:scale-110'}`}
        />
        {isExpanded && (
          <>
            <span className="relative z-10 flex-1 whitespace-nowrap text-left">{label}</span>
            <ChevronDownIcon className={`relative z-10 h-4 w-4 shrink-0 transition-transform duration-200
              ${isActive ? 'text-white' : 'text-gray-400'}
              ${isOpen ? 'rotate-180' : 'rotate-0'}`}
            />
          </>
        )}
        <span className={`absolute right-2 w-1.5 h-1.5 rounded-full transition-opacity duration-150 ${
          isActive && isExpanded ? 'bg-white opacity-100' : 'bg-[#C5A059] opacity-0 group-hover:opacity-100'
        }`} />
      </button>

      {isExpanded && isOpen && (
        <div className="mt-1 ml-4 pl-3 border-l-2 border-[#D4EDDA] space-y-0.5">
          {subLinks.map(sub => {
            const SubIcon = sub.icon;
            const isSubActive = sub.exact
              ? location.pathname === sub.to
              : location.pathname === sub.to || location.pathname.startsWith(sub.to + '/');
            return (
              <Link
                key={sub.to}
                to={sub.to}
                onClick={() => isMobile && setIsSidebarOpen(false)}
                className={`group relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium
                  overflow-hidden transition-all duration-150
                  ${isSubActive
                    ? 'text-white bg-gradient-to-r from-[#2D6A4F]/90 to-[#C27B7F]/90 shadow-sm'
                    : 'text-gray-600 hover:text-[#2D6A4F] hover:bg-[#D4EDDA]/50'
                  }`}
              >
                <SubIcon className={`h-4 w-4 shrink-0 ${
                  isSubActive ? 'text-white' : 'text-gray-400 group-hover:text-[#2D6A4F]'
                }`} />
                <span className="whitespace-nowrap">{sub.label}</span>
                {isSubActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white shrink-0" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#D4EDDA]/20 via-white to-[#F9E2E7]/20">

      {/* Mobile Header */}
      <div className="lg:hidden bg-white/80 backdrop-blur-sm border-b border-[#D4EDDA] sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <button onClick={() => setIsSidebarOpen(true)}
            className="p-2 rounded-lg text-[#2D6A4F] hover:bg-[#D4EDDA]/50 transition-colors">
            <Bars3Icon className="h-5 w-5" />
          </button>
          <Link to="/admin" className="flex items-center gap-2 group">
            <div className="relative">
              <div className="w-7 h-7 bg-gradient-to-r from-[#2D6A4F] to-[#C5A059] rounded-lg flex items-center justify-center group-hover:rotate-6 transition-transform duration-200">
                <span className="text-white font-semibold text-sm">A</span>
              </div>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#C27B7F] rounded-full animate-pulse" />
            </div>
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#2D6A4F] to-[#C27B7F]">
              Admin Panel
            </span>
          </Link>
          <div className="w-9" />
        </div>
      </div>

      {/* Mobile Backdrop */}
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-0 bg-[#2D6A4F]/40 backdrop-blur-sm z-40"
          onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        style={{
          width: sidebarW,
          transform: isMobile && !isSidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'width 150ms ease-out, transform 150ms ease-out',
          willChange: 'width, transform',
        }}
        className="fixed inset-y-0 left-0 z-50 bg-white/95 backdrop-blur-sm border-r border-[#D4EDDA] shadow-xl overflow-hidden"
      >
        <div className="flex flex-col h-full">

          {/* Sidebar Header */}
          <div
            className="relative h-20 flex items-center border-b border-[#D4EDDA] bg-gradient-to-r from-[#D4EDDA]/30 to-[#F9E2E7]/30"
            style={{ padding: isExpanded ? '0 1.5rem' : '0', justifyContent: isExpanded ? 'space-between' : 'center' }}
          >
            {!isExpanded ? (
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-r from-[#2D6A4F] to-[#C5A059] rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-lg">A</span>
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#C27B7F] rounded-full animate-ping" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#C27B7F] rounded-full" />
              </div>
            ) : (
              <>
                <Link to="/admin" className="flex items-center gap-2 group flex-1 min-w-0">
                  <div className="relative shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-r from-[#2D6A4F] to-[#C5A059] rounded-xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-200 shadow-lg">
                      <span className="text-white font-bold text-lg">A</span>
                    </div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#C27B7F] rounded-full animate-ping" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#C27B7F] rounded-full" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2D6A4F] to-[#C27B7F] text-lg whitespace-nowrap">
                      Admin Panel
                    </span>
                    <p className="text-xs text-[#C5A059]">Management</p>
                  </div>
                </Link>
                {!isMobile && (
                  <button onClick={() => setIsCollapsed(!isCollapsed)}
                    className="p-1 rounded-md text-[#2D6A4F] hover:bg-[#D4EDDA] transition-colors shrink-0"
                    title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                    <ChevronRightIcon className="h-5 w-5 transition-transform duration-150"
                      style={{ transform: !isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  </button>
                )}
                {isMobile && (
                  <button onClick={() => setIsSidebarOpen(false)}
                    className="p-1 rounded-md text-[#2D6A4F] hover:bg-[#D4EDDA] transition-colors shrink-0">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
          </div>

          {/* Gradient accent */}
          <div className="h-0.5 w-full bg-gradient-to-r from-[#2D6A4F] via-[#C5A059] to-[#C27B7F] shrink-0" />

          {/* Nav Links */}
          <div className="flex-1 overflow-y-auto py-4 px-2 scrollbar-thin scrollbar-thumb-[#D4EDDA] scrollbar-track-transparent">
            <nav className="space-y-1">

              {/* Section 1: Dashboard to Inventory Purchases */}
              {topLinks.map(link => (
                <NavLink key={link.to} to={link.to} icon={link.icon} label={link.label} exact={link.exact} />
              ))}

              {/* ── ORDERS DROPDOWN (UPDATED with Place Order) ── */}
              <NavDropdown
                icon={ClipboardDocumentListIcon}
                label="Orders"
                isActive={isOrdersActive}
                isOpen={isOrdersOpen}
                onToggle={() => setIsOrdersOpen(p => !p)}
                onCollapsedClick={() => navigate('/admin/orders')}
                subLinks={orderSubLinks} // ✅ Now includes Place Order
              />

              {/* ── COUPONS DROPDOWN ── */}
              <NavDropdown
                icon={TicketIcon}
                label="Coupons"
                isActive={isCouponsActive}
                isOpen={isCouponsOpen}
                onToggle={() => setIsCouponsOpen(p => !p)}
                onCollapsedClick={() => navigate('/admin/coupons/free-gift')}
                subLinks={couponSubLinks}
              />

              {/* Section 2: All Customers - SINGLE LINK (NO DROPDOWN) */}
              {customerLink.map(link => (
                <NavLink key={link.to} to={link.to} icon={link.icon} label={link.label} exact={link.exact} />
              ))}

              {/* Section 3: Employees to Invoice & Charges */}
              {bottomLinks.map(link => (
                <NavLink key={link.to} to={link.to} icon={link.icon} label={link.label} exact={link.exact} />
              ))}

            </nav>
          </div>

          {/* User Info & Logout */}
          <div
            className="relative p-3 border-t border-[#D4EDDA] bg-gradient-to-b from-white to-[#F9E2E7]/30 shrink-0"
            style={{ textAlign: !isExpanded ? 'center' : undefined }}
          >
            <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-[#C5A059] opacity-30 pointer-events-none" />
            <div className={`flex items-center gap-3 mb-3 ${!isExpanded ? 'justify-center' : ''}`}>
              <div className="relative shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-[#2D6A4F] to-[#C27B7F] rounded-xl flex items-center justify-center shadow-lg">
                  <UserIcon className="h-5 w-5 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" />
              </div>
              {isExpanded && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.first_name || user?.email?.split('@')[0] || 'Admin'}
                  </p>
                  <p className="text-xs text-[#C27B7F] truncate flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-[#C5A059] rounded-full shrink-0" />
                    {user?.role || 'Administrator'}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              title={!isExpanded ? 'Logout' : undefined}
              className="group relative w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-white border-2 border-[#D4EDDA] rounded-xl text-sm font-medium text-[#2D6A4F] hover:shadow-md transition-shadow duration-150 overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-[#D4EDDA] to-[#F9E2E7] opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
              <ArrowRightOnRectangleIcon className="relative z-10 h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-150 shrink-0" />
              {isExpanded && <span className="relative z-10">Logout</span>}
            </button>
          </div>

        </div>
      </aside>

      {/* Main Content */}
      <main style={{ paddingLeft: !isMobile ? sidebarW : 0, transition: 'padding-left 150ms ease-out' }}>
        <div
          className="hidden lg:block fixed top-0 right-0 h-0.5 bg-gradient-to-r from-[#2D6A4F] via-[#C5A059] to-[#C27B7F] z-50"
          style={{ left: sidebarW, transition: 'left 150ms ease-out' }}
        />
        <div className="relative p-4 sm:p-6 lg:p-8">
          <div className="pointer-events-none absolute top-20 right-20 w-64 h-64 bg-[#D4EDDA] rounded-full blur-3xl opacity-20" />
          <div className="pointer-events-none absolute bottom-20 left-20 w-96 h-96 bg-[#F9E2E7] rounded-full blur-3xl opacity-20" />
          <div className="relative z-10"><Outlet /></div>
        </div>
      </main>

      <style>{`
        .scrollbar-thin::-webkit-scrollbar       { width: 4px; }
        .scrollbar-thin::-webkit-scrollbar-track  { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb  { background: #D4EDDA; border-radius: 20px; }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #C5A059; }
      `}</style>
    </div>
  );
};

export default AdminLayout;